import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { BRAND } from "@/lib/biohackerBrand";

/**
 * Platform — the intelligence pipeline: Assessment → Profile → Cloud →
 * Engines → AI → Predictive → Coach/Department dashboards → Enterprise.
 */

const FLOW = [
  { step: "Athlete Intelligence Assessment™",
    body: "Every athlete begins with a comprehensive baseline across all 8 Intelligence Domains™." },
  { step: "Athlete Intelligence Profile™ (AIP™)",
    body: "A single, continuously-evolving record of the athlete's movement, performance, health, recovery, workload, development, mental state, and vision." },
  { step: "BioHacker Intelligence Cloud™",
    body: "Every data point from every device streams into one cloud, one athlete, one shared record — trainers, S&C, coaches, medicine, and admin see the same truth." },
  { step: "Intelligence Engines™",
    body: "11 productized capability engines (ArmIQ™, MotionIQ™, SpeedIQ™, StrengthIQ™, RecoveryIQ™, VisionIQ™, MindIQ™, CoachIQ™, TeamIQ™, ScoutIQ™, AthleteIQ™) turn the profile into decisions." },
  { step: "AI Intelligence™",
    body: "LLM + biomechanical models translate multi-domain signals into plain-language coaching insight." },
  { step: "Predictive Analytics™",
    body: "Injury risk, readiness, return-to-play, and development trajectories forecast weeks ahead — not after the fact." },
  { step: "Coach Dashboards™",
    body: "Team-level surface for practice planning, roster availability, and individualized workflows." },
  { step: "Department Dashboards™",
    body: "Multi-role S&C + medical + coaching visibility with permission-scoped analytics." },
  { step: "Enterprise Intelligence™",
    body: "Cross-team, cross-site, cross-sport visibility for athletic directors, executives, and federations." },
];

export default function Platform() {
  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid="platform-page">
      <SiteNav />
      <section className="pt-32 pb-14 border-b border-white/8 max-w-7xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">The Platform</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]">
          One continuous intelligence pipeline.<br/>
          <span className="text-white/50">Athlete → Profile → Cloud → Engines → Decisions.</span>
        </h1>
        <p className="mt-6 text-white/70 max-w-2xl">
          The Athlete Performance Operating System&trade; is not a features list. It&apos;s a data model that keeps
          every athlete&apos;s intelligence flowing from the moment they walk in the door to the moment they graduate.
        </p>
        <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-5 max-w-3xl" data-testid="platform-philosophy">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-fuchsia-300/80 mb-2">The philosophy</div>
          <p className="text-white/85 leading-relaxed">
            Modern athletic performance requires modern structural infrastructure. Every jump, cut, throw, and land is
            <span className="text-white font-semibold"> hundreds to thousands of pounds of measurable kinetic force</span> — and unmonitored
            load is the difference between a scholarship season and a surgical table. BioHacker Sports&trade; retires the era
            of 1980s foam and gut-feel and replaces it with <span className="text-white font-semibold">quantified bio-mitigation</span>
            — the same physics the pros use, defensible on the AD&apos;s balance sheet, actionable on the trainer&apos;s clipboard.
          </p>
        </div>
      </section>

      <section className="py-16 max-w-4xl mx-auto px-6" data-testid="platform-flow">
        <ol className="space-y-4">
          {FLOW.map((f, i) => (
            <li key={f.step}>
              <div className="ink-card rounded-2xl p-6 relative" data-testid={`platform-step-${i}`}>
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 mb-2">Step {i + 1}</div>
                <div className="font-display font-black text-xl text-white">{f.step}</div>
                <div className="mt-2 text-sm text-white/70 leading-relaxed">{f.body}</div>
              </div>
              {i < FLOW.length - 1 && (
                <div className="flex justify-center py-2 text-white/25">
                  <ArrowDown className="h-5 w-5" />
                </div>
              )}
            </li>
          ))}
        </ol>
        <div className="mt-12 text-center">
          <Link to="/request-demo">
            <Button size="lg" className="text-white font-bold text-sm px-6 py-6" style={{ backgroundColor: "var(--brand-secondary)" }}>
              Request demo <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
